import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/components/store/Store';
import Container from '@/components/Container';
import { Typography, Button, Select, MenuItem } from '@mui/material';
import { Api } from '@/services/Api';
import { AvailableDesigns } from '@/declarations/models/Designs';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { ToolbarHeader } from '@/components/ToolbarHeader';
import { formatAPITimestamp } from '@/utils/dates';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import FullScreenModal from '@/components/FullScreenModal';
import { DesignEditor } from './AdminDesignEditor';

export const AdminDesign: FC<ChildrenProp> = () => {
  const [designs, setDesigns] = useState<AvailableDesigns>({ designs: { global: [], owner: [], site: [] } });
  const {
    state: { selectedSite, selectedSiteData },
  } = useStore();

  const { t: tCommon } = useTranslation('common');

  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editDesignId, setEditDesignId] = useState<number>();
  const onAddNewDesign = () => {
    setEditDesignId(0);
    setEditModalOpen(true);
  };

  const [refreshDep, setRefreshDep] = useState(Date.now);
  const refreshPage = () => setRefreshDep(Date.now());

  const handleCloseModal = () => {
    refreshPage();
    setEditModalOpen(false);
  };

  const handleOnItemClick = (child: any) => {
    setEditDesignId(child.id);
    setEditModalOpen(true);
  };

  /* If goint to editor by route
  const onEditDesign = (id: number) => {
    navigate(`/m24admin/design/editor/${id}`);
  };
  */

  useEffect(() => {
    Api.getAllDesigns(selectedSite?.id || 0)
      .fetch()
      .then((data) => {
        setDesigns((data[0] as AvailableDesigns) || []);
      });
  }, [selectedSite, refreshDep]);

  const [level, setLevelState] = useState<'global' | 'owner' | 'site'>('site');

  function setLevel(value: any) {
    setLevelState(value);
  }

  const getLevelLabel = () => {
    if (level === 'global') return 'Globale';
    if (level === 'owner') return 'Eier';
    return 'Nettsted';
  };

  const getLevelButtonLabel = () => {
    if (level === 'global') return 'Globalt';
    if (level === 'owner') return 'Eier';
    return 'Nettsted';
  };
  return (
    <Container left fullWidth p={2} top mt={0} column>
      <FullScreenModal modalOpen={editModalOpen}>
        <Container fullWidth left>
          <DesignEditor
            designId={editDesignId}
            isGlobal={level === 'global'}
            ownerId={level === 'owner' ? selectedSite?.owner?.id : null}
            siteId={level === 'site' ? selectedSite?.id : null}
            onCloseEditor={handleCloseModal}
          />
        </Container>
      </FullScreenModal>

      <ToolbarHeader
        heading={
          <Typography variant='h3' sx={{ component: 'h1', fontSize: 30 }}>
            Stilsett ({getLevelLabel()})
          </Typography>
        }
        topRight={
          <Button
            onClick={onAddNewDesign}
            variant='contained'
            color='success'
            startIcon={<MaterialSymbol name='add' />}
            type='button'>
            Nytt stilsett ({getLevelButtonLabel()})
          </Button>
        }
      />

      <Container fullWidth left top sx={{ marginTop: '16px', height: '48px' }}>
        <Typography variant='subtitle1' sx={{ marginRight: 2 }}>
          Nivå:
        </Typography>
        <Select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': 'Without label' }}
          fullWidth>
          <MenuItem value='global'>Globale</MenuItem>
          <MenuItem value='owner'>
            Museum (felles): {selectedSiteData?.owner?.parent?.name || '(NO-PARENT)'} /{' '}
            <strong>{selectedSite?.owner?.name || '-'}</strong>
          </MenuItem>
          <MenuItem value='site'>Nettsted: {selectedSite?.name || '-'}</MenuItem>
        </Select>
      </Container>

      {level === 'owner' && (
        <p>
          <strong>
            NB! Merk at eier-stilsett normalt registreres på konsoliderte eiere (som har museer under seg), eller eiere
            som har flere nettsteder.
          </strong>{' '}
          <br />
          {selectedSiteData?.owner?.name} har {selectedSiteData?.owner?.children?.length} museer under seg samt{' '}
          {selectedSiteData?.sibling_count || 0} egne nettsteder.
          <br />
          <br />
          Tabellen under viser alle stilsett som er tilgjengelige for valgt nettsted/eier, dvs at disse kan være
          registrert på f.eks på konsolidert nivå og du må stå på samme nivå i nettstedsvelger for å kunne redigere.
        </p>
      )}

      <Container left fullWidth pt={3} top mt={0} column>
        <TableContainer component={Paper}>
          <Table className='compact-table' stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Tittel</TableCell>
                <TableCell>Nivå</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Opprettet</TableCell>
                <TableCell>Endret</TableCell>
                <TableCell>Skins / Themes </TableCell>
                <TableCell align='center'>I bruk?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {designs.designs[level]
                .sort((a, b) => {
                  const aUpdated = a.updated_at ?? '';
                  const bUpdated = b.updated_at ?? '';
                  return aUpdated < bUpdated ? 1 : -1;
                })
                .map((child) => (
                  <TableRow key={child.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell align='center' width={50}>
                      {child.id}
                    </TableCell>
                    <TableCell scope='row'>
                      {child.owner_id && selectedSiteData?.owner_id === child.owner_id && (
                        <Button
                          style={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                          onClick={() => handleOnItemClick(child)}>
                          {child.title}
                        </Button>
                      )}

                      {child.is_global && (
                        <Button
                          style={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                          onClick={() => handleOnItemClick(child)}>
                          {child.title}
                        </Button>
                      )}

                      {child.site_id && selectedSite?.id === child.site_id && (
                        <Button
                          style={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                          onClick={() => handleOnItemClick(child)}>
                          {child.title}
                        </Button>
                      )}

                      {!(
                        child.is_global ||
                        (child.owner_id && selectedSiteData?.owner_id === child.owner_id) ||
                        (child.site_id && selectedSite?.id === child.site_id)
                      ) && <span>{child.title}</span>}
                    </TableCell>
                    <TableCell>
                      {child.is_global ? 'Global' : ''}
                      {child.owner_id ? 'Museum ID: ' : ''} {child.owner_id ? child.owner_id : ''}
                      {child.site_id ? 'Nettsted ' : ''}
                    </TableCell>
                    <TableCell>{tCommon(`Status.${child.status}`)}</TableCell>
                    <TableCell>{formatAPITimestamp(child.created_at || '', 'date') || '-'}</TableCell>
                    <TableCell>{formatAPITimestamp(child.updated_at || '', 'date') || '-'}</TableCell>
                    <TableCell>
                      {child?.skins.length || 0} skins / {child?.themes.length || 0} themes
                    </TableCell>
                    <TableCell align='center'>{child.is_in_use ? 'Ja' : '-'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Container>
  );
};

export default AdminDesign;
